import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock, Spinner } from '../../components/Spinner'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { Sentinel } from '../../library/Sentinel'
import { useToast } from '../../library/useToast'
import { Avatar } from '../Avatar'
import { createOfApi, OF } from '../ofApi'
import { actionOf, cacheUser, createdAt, fanIdOf, fanView, normalizeUsersList, timeLabel, type UserCache } from '../notifications/notificationsData'
import { Tabs } from '../settings/kit'
import type { MorePageProps } from './morePage'
import { copyToClipboard } from './releaseForms/copy'
import { AddToModal, ChooserModal, DocsModal, GuestModal, LinkModal, Popover } from './releaseForms/RfModals'
import {
  buildRoute,
  counter,
  DEFAULT_LIMIT,
  DEFAULT_OPTIONS,
  formatDay,
  formsQuery,
  fromNow,
  isFiltered,
  isGuest,
  isHidden,
  isIdentityVerified,
  isLinkExpired,
  isLinkForm,
  isRowDisabled,
  isSigned,
  isViewed,
  LIST_PATH,
  mentionsQuery,
  MENTIONS_PATH,
  mergeItems,
  parseMentions,
  parsePage,
  parseRoute,
  pickOption,
  REFERER,
  SEARCH_MAX,
  SEARCH_MIN,
  SELECT_MAX,
  selectKey,
  showCopyLinkBtn,
  showEditBtn,
  showExpirationWarning,
  TOGGLE_SHOW_PATH,
  toggleShowBody,
  usersQuery,
  USERS_LIMIT,
  USERS_SEARCH_LIMIT,
  type Raw,
  type RfItem,
  type RfListTab,
  type RfOptions,
  type RfTab,
} from './releaseForms/releaseFormsData'
import '../../library/library.css'
import '../ofPages.css'
import '../settings/kit.css'
import './releaseForms/releaseForms.css'

/** What users/me says about release forms. */
interface Me {
  isPerformer: boolean
  newTagsCount: number
  needTagCount: number
  rfDocAllowedUploads: number
}

interface ListState {
  items: RfItem[]
  hasMore: boolean
  offset: number
  status: 'idle' | 'loading' | 'ready' | 'error'
  more: boolean
  error: string
}

interface MentionsState {
  rows: Raw[]
  hasMore: boolean
  offset: number
  lastId: string
  status: 'idle' | 'loading' | 'ready' | 'error'
  more: boolean
  error: string
}

interface Picked {
  key: string
  item: RfItem
}

type Dialog =
  | { kind: 'guest'; item: RfItem | null }
  | { kind: 'link'; item: RfItem | null }
  | { kind: 'docs'; item: RfItem | null }
  | { kind: 'chooser' }
  | { kind: 'addTo' }

const EMPTY_LIST: ListState = { items: [], hasMore: false, offset: 0, status: 'idle', more: false, error: '' }
const EMPTY_MENTIONS: MentionsState = { rows: [], hasMore: false, offset: 0, lastId: '', status: 'idle', more: false, error: '' }

const errText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback)
const num = (v: unknown) => (typeof v === 'number' ? v : Number(v) || 0)

/** Wide enough for OnlyFans' right-hand column (its isLgMQ), measured on the page itself. */
function useWide(ref: React.RefObject<HTMLElement | null>, min = 960) {
  const [wide, setWide] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([entry]) => entry && setWide(entry.contentRect.width >= min))
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref, min])
  return wide
}

/** OnlyFans' sort icon: three lines, shorter downwards. */
function SortIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="15" y2="12" />
      <line x1="3" y1="18" x2="9" y2="18" />
    </svg>
  )
}

/**
 * Release forms (onlyfans.com/my/release-forms): TAG USER (creators tagged in
 * posts and guest invitations), RELEASE FORM (signing links and uploaded
 * forms) and MENTIONS, with search, sort, hide/unhide, rename, new invitation
 * and release form links, and the selection bar.
 */
export function ReleaseFormsPage({ canWrite, query, onOpenPage, mobileNav }: MorePageProps) {
  const { t, i18n } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  const { gateway } = useLibraryDeps()
  const of = useMemo(() => createOfApi(gateway), [gateway])
  const mobile = useIsNarrow()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const wide = useWide(rootRef)
  const { say, node: toast } = useToast()

  const route = useMemo(() => parseRoute(query), [query])
  const [tab, setTab] = useState<RfTab>(route.tab)
  const [searchBy, setSearchBy] = useState<RfListTab | null>(route.searchBy)
  const [searchText, setSearchText] = useState(route.q)
  const [searchInput, setSearchInput] = useState(route.q)
  useEffect(() => {
    setTab(route.tab)
    setSearchBy(route.searchBy)
  }, [route])

  const [me, setMe] = useState<Me | null>(null)
  const [meError, setMeError] = useState('')
  const [options, setOptions] = useState<Record<RfListTab, RfOptions>>({ users: DEFAULT_OPTIONS, forms: DEFAULT_OPTIONS })
  const [lists, setLists] = useState<Record<RfListTab, ListState>>({ users: EMPTY_LIST, forms: EMPTY_LIST })
  const listsRef = useRef(lists)
  listsRef.current = lists
  const gens = useRef<Record<RfListTab, number>>({ users: 0, forms: 0 })
  const [mentions, setMentions] = useState<MentionsState>(EMPTY_MENTIONS)
  const mentionsRef = useRef(mentions)
  mentionsRef.current = mentions
  const mentionsGen = useRef(0)
  const [users, setUsers] = useState<UserCache>({})
  const askedUsers = useRef(new Set<string>())
  const [selected, setSelected] = useState<Picked[]>([])
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const optionsRef = useRef(options)
  optionsRef.current = options
  const searchRef = useRef(searchText)
  searchRef.current = searchText

  const isPerformer = me?.isPerformer ?? true
  const isMentions = tab === 'mentions' || !isPerformer
  const listTab: RfListTab = tab === 'forms' ? 'forms' : 'users'
  const isSearchMode = !!searchBy || searchText.length > 0

  // ── Loading ───────────────────────────────────────────────────────────────

  const loadMe = useCallback(async () => {
    setMeError('')
    try {
      const raw = await of.get<Raw>('users/me', {}, REFERER)
      setMe({
        isPerformer: raw?.isPerformer !== false,
        newTagsCount: num(raw?.newTagsCount),
        needTagCount: num(raw?.needTagCount),
        rfDocAllowedUploads: num(raw?.rfDocAllowedUploads),
      })
    } catch (e) {
      setMeError(errText(e, t('error')))
    }
  }, [of, t])
  useEffect(() => {
    void loadMe()
  }, [loadMe])

  /** fetchUsersPage / fetchFormsPage: a first page (resets the list) or the next one. */
  const fetchList = useCallback(
    async (list: RfListTab, { more = false, limit, name }: { more?: boolean; limit: number; name?: string }) => {
      const gen = more ? gens.current[list] : ++gens.current[list]
      const prev = listsRef.current[list]
      const offset = more ? prev.offset : 0
      setLists((all) => ({ ...all, [list]: more ? { ...all[list], more: true } : { ...EMPTY_LIST, status: 'loading' } }))
      const args = { limit, offset, options: optionsRef.current[list], name: name ?? searchRef.current }
      try {
        const data = await of.get(LIST_PATH[list], list === 'users' ? usersQuery(args) : formsQuery(args), REFERER)
        if (gen !== gens.current[list]) return
        const page = parsePage(data)
        setLists((all) => ({
          ...all,
          [list]: {
            items: more ? mergeItems(all[list].items, page.items) : page.items,
            hasMore: page.hasMore,
            offset: offset + limit,
            status: 'ready',
            more: false,
            error: '',
          },
        }))
      } catch (e) {
        if (gen !== gens.current[list]) return
        setLists((all) => ({ ...all, [list]: more ? { ...all[list], more: false, error: errText(e, t('error')) } : { ...EMPTY_LIST, status: 'error', error: errText(e, t('error')) } }))
      }
    },
    [of, t],
  )

  /** users/list?m[]=… for the mentioned creators, ten at a time (getUsersByIds). */
  const resolveUsers = useCallback(
    async (rows: Raw[]) => {
      const ids = [...new Set(rows.map(fanIdOf).filter((id) => id && !askedUsers.current.has(id)))]
      ids.forEach((id) => askedUsers.current.add(id))
      for (let i = 0; i < ids.length; i += 10) {
        const chunk = ids.slice(i, i + 10)
        try {
          const payload = await of.get('users/list', { 'm[]': chunk }, REFERER)
          const found: UserCache = {}
          for (const u of normalizeUsersList(payload)) if (u.id) found[String(u.id)] = cacheUser(u)
          setUsers((all) => ({ ...all, ...found }))
        } catch {
          chunk.forEach((id) => askedUsers.current.delete(id))
        }
      }
    },
    [of],
  )

  /** getMentions: limit 10, offset + fromId paging, one retry from lastId on an empty page. */
  const fetchMentions = useCallback(
    async (more: boolean) => {
      const gen = more ? mentionsGen.current : ++mentionsGen.current
      const prev = mentionsRef.current
      const offset = more ? prev.offset : 0
      const fromId = more ? prev.lastId || undefined : undefined
      setMentions((m) => (more ? { ...m, more: true } : { ...EMPTY_MENTIONS, status: 'loading' }))
      try {
        let page = parseMentions(await of.get(MENTIONS_PATH, mentionsQuery(offset, fromId), REFERER))
        if (!page.list.length && page.hasMore && page.lastId) {
          const again = parseMentions(await of.get(MENTIONS_PATH, mentionsQuery(offset, page.lastId), REFERER))
          page = { list: again.list, hasMore: again.hasMore, lastId: again.lastId || page.lastId }
        }
        if (gen !== mentionsGen.current) return
        setMentions((m) => ({
          rows: more ? [...m.rows, ...page.list] : page.list,
          hasMore: page.hasMore,
          offset: offset + DEFAULT_LIMIT,
          lastId: page.lastId,
          status: 'ready',
          more: false,
          error: '',
        }))
        // Seeing the mentions clears the MENTIONS badge (users/setAuthUserData newTagsCount: 0).
        setMe((m) => (m ? { ...m, newTagsCount: 0 } : m))
        void resolveUsers(page.list)
      } catch (e) {
        if (gen !== mentionsGen.current) return
        setMentions((m) => (more ? { ...m, more: false, error: errText(e, t('error')) } : { ...EMPTY_MENTIONS, status: 'error', error: errText(e, t('error')) }))
      }
    },
    [of, t, resolveUsers],
  )

  // The open tab loads when it opens (OnlyFans' list components on mount): users 50, forms 10.
  useEffect(() => {
    if (!me) return
    if (isMentions) void fetchMentions(false)
    else void fetchList(listTab, { limit: listTab === 'users' ? USERS_LIMIT : DEFAULT_LIMIT })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me === null, isMentions, listTab])

  // ── Navigation ────────────────────────────────────────────────────────────

  const go = useCallback(
    (next: { tab: RfTab; searchBy?: RfListTab | null }) => {
      setTab(next.tab)
      setSearchBy(next.searchBy ?? null)
      onOpenPage('releaseforms', buildRoute(next), { replace: true })
    },
    [onOpenPage],
  )
  const changeTab = (next: RfTab) => {
    if (next === tab) return
    go({ tab: next, searchBy: searchBy && next !== 'mentions' ? next : null })
  }
  const goToSearch = (by: RfListTab) => go({ tab: by, searchBy: by })
  const exitSearch = () => {
    setSearchText('')
    setSearchInput('')
    searchRef.current = ''
    go({ tab: listTab, searchBy: null })
    void fetchList(listTab, { limit: listTab === 'users' ? USERS_LIMIT : DEFAULT_LIMIT, name: '' })
  }

  /** ReleaseFormHeader.searchSubmit: users 20 per page by name (50 without), forms 10. */
  const runSearch = useCallback(
    (name: string) => {
      setSearchText(name)
      searchRef.current = name
      void fetchList(listTab, { name, limit: listTab === 'users' ? (name ? USERS_SEARCH_LIMIT : USERS_LIMIT) : DEFAULT_LIMIT })
    },
    [fetchList, listTab],
  )

  // Typing searches after 500ms from three characters on (or when cleared).
  const lastTyped = useRef(searchInput)
  useEffect(() => {
    if (searchInput === lastTyped.current) return
    const id = window.setTimeout(() => {
      lastTyped.current = searchInput
      if (searchInput && searchInput.length < SEARCH_MIN) {
        setSearchText(searchInput)
        searchRef.current = searchInput
        return
      }
      runSearch(searchInput)
    }, 500)
    return () => window.clearTimeout(id)
  }, [searchInput, runSearch])

  /** ReleaseFormsFilter → onFilter: the list again from the top, 20 by name / 50. */
  const changeOption = (group: keyof RfOptions, value: string) => {
    const next = pickOption(options[listTab], group, value)
    optionsRef.current = { ...optionsRef.current, [listTab]: next }
    setOptions(optionsRef.current)
    void fetchList(listTab, { limit: searchText ? USERS_SEARCH_LIMIT : USERS_LIMIT })
  }

  const loadMore = useCallback(() => {
    const l = listsRef.current[listTab]
    if (l.status !== 'ready' || l.more || !l.hasMore || l.error) return
    void fetchList(listTab, { more: true, limit: listTab === 'users' ? (searchRef.current ? USERS_SEARCH_LIMIT : USERS_LIMIT) : DEFAULT_LIMIT })
  }, [fetchList, listTab])
  const loadMoreMentions = useCallback(() => {
    const m = mentionsRef.current
    if (m.status !== 'ready' || m.more || !m.hasMore || m.error) return
    void fetchMentions(true)
  }, [fetchMentions])

  // ── Actions ───────────────────────────────────────────────────────────────

  const selectedKeys = useMemo(() => new Set(selected.map((p) => p.key)), [selected])
  const toggleSelected = (item: RfItem, list: RfListTab) => {
    const key = selectKey(item, list)
    setSelected((all) => (all.some((p) => p.key === key) ? all.filter((p) => p.key !== key) : all.length >= SELECT_MAX ? all : [...all, { key, item }]))
  }

  const updateItem = (list: RfListTab, id: RfItem['id'], patch: (i: RfItem) => RfItem | null) =>
    setLists((all) => ({ ...all, [list]: { ...all[list], items: all[list].items.flatMap((i) => (i.id === id ? (patch(i) ?? []) : [i])) } }))

  /** hideReleaseForms: PATCH release-forms/toggle-show; a hidden row leaves the default list. */
  const toggleHide = async (item: RfItem, list: RfListTab) => {
    if (!canWrite) return
    try {
      const res = (await of.send<Raw>('PATCH', TOGGLE_SHOW_PATH, REFERER, toggleShowBody(item))) ?? {}
      const hidden = typeof res.isHidden === 'boolean' ? res.isHidden : !isHidden(item)
      const viewToHidden = !searchText
      updateItem(list, item.id, (i) => (hidden && viewToHidden ? null : { ...i, hidden, user: i.user ? { ...i.user, hidden } : i.user }))
      say(t(hidden ? 'toast.removed' : 'toast.back'))
    } catch (e) {
      say(errText(e, t('error')))
    }
  }

  /** A saved link / guest goes to the top of its list (saveReleaseLink, saveGuest, renameReleaseForms). */
  const putOnTop = (list: RfListTab, saved: RfItem) =>
    setLists((all) => {
      const l = all[list]
      const old = l.items.find((i) => i.id === saved.id)
      const merged = old ? { ...old, ...saved, user: saved.user ?? old.user } : saved
      return { ...all, [list]: { ...l, status: l.status === 'idle' ? 'ready' : l.status, items: [merged, ...l.items.filter((i) => i.id !== saved.id)] } }
    })
  const renameSelected = (saved: RfItem) => setSelected((all) => all.map((p) => (p.item.id === saved.id ? { ...p, item: { ...p.item, name: saved.name } } : p)))

  const openNewForm = () => setDialog(me && me.rfDocAllowedUploads > 0 ? { kind: 'chooser' } : { kind: 'link', item: null })
  const copy = (kind: Parameters<typeof copyToClipboard>[0], v: Parameters<typeof copyToClipboard>[1]) => void copyToClipboard(kind, v, say, t)

  // ── Render ────────────────────────────────────────────────────────────────

  const list = lists[listTab]
  const opts = options[listTab]
  const filtered = isFiltered(opts)
  const loadingList = list.status === 'loading' || list.status === 'idle'
  const firstTime = !list.items.length && !loadingList
  const formsAvailable = !isMentions && (!loadingList || filtered)
  const mentionsBadge = counter(me?.newTagsCount, 99)
  const tabs = [
    { id: 'users' as const, label: t('tabs.users') },
    { id: 'forms' as const, label: t('tabs.forms') },
    ...(isSearchMode ? [] : [{ id: 'mentions' as const, label: mentionsBadge ? <MentionsLabel text={t('tabs.mentions')} badge={mentionsBadge} /> : t('tabs.mentions') }]),
  ]

  const widgets = (type: RfTab, both: boolean) => (
    <GroupWidgets
      showUsers={both || type === 'users'}
      showForms={both || type === 'forms'}
      busy={lists.forms.status === 'loading'}
      canWrite={canWrite}
      uploads={(me?.rfDocAllowedUploads ?? 0) > 0}
      onInvite={() => setDialog({ kind: 'guest', item: null })}
      onNewForm={openNewForm}
    />
  )

  let body: ReactNode
  if (!me) {
    body = meError ? <ErrorBox text={t('loadFailed', { error: meError })} onRetry={() => void loadMe()} /> : <LoadingBlock label={t('loading')} />
  } else if (isMentions) {
    body = <MentionsList state={mentions} users={users} onRetry={() => void fetchMentions(false)} onMore={loadMoreMentions} lang={i18n.language} />
  } else if (list.status === 'error') {
    body = <ErrorBox text={t('loadFailed', { error: list.error })} onRetry={() => void fetchList(listTab, { limit: listTab === 'users' ? USERS_LIMIT : DEFAULT_LIMIT })} />
  } else if (loadingList) {
    body = <LoadingBlock label={t('loading')} />
  } else if (!list.items.length) {
    body = <EmptyState icon={isSearchMode ? 'search' : listTab === 'users' ? 'at-sign' : 'file-text'} text={isSearchMode ? '' : t(listTab === 'users' ? 'empty.users' : 'empty.forms')} />
  } else {
    body = (
      <div className="ofr-list" role="list">
        {list.items.map((item) => {
          const key = selectKey(item, listTab)
          const props = {
            item,
            checked: selectedKeys.has(key),
            disabled: !canWrite || isRowDisabled(item, selectedKeys, key),
            canWrite,
            onToggle: () => toggleSelected(item, listTab),
            onHide: () => void toggleHide(item, listTab),
          }
          return listTab === 'users' ? (
            <UserRow
              key={key}
              {...props}
              onCopy={(kind) => (kind === 'guest' ? copy('guest', { token: item.token }) : copy(kind, { username: item.user?.username }))}
              onEdit={() => setDialog({ kind: 'guest', item })}
            />
          ) : (
            <FormRow
              key={key}
              {...props}
              lang={i18n.language}
              onCopy={() => copy('link', { code: item.code, signature: item.signature })}
              onEdit={() => setDialog(isLinkForm(item.type) ? { kind: 'link', item } : { kind: 'docs', item })}
            />
          )
        })}
        {list.hasMore && (
          <div className="ofr-more">
            {list.more ? (
              <Spinner size={22} label={t('loading')} />
            ) : list.error ? (
              <button type="button" className="ofr-text-link" onClick={loadMore}>
                {t('retry')}
              </button>
            ) : (
              <Sentinel onVisible={loadMore} token={list.items.length} />
            )}
          </div>
        )}
      </div>
    )
  }

  const sortTitle = t(`sortTitle.${opts.sort}`)

  return (
    <div ref={rootRef} className={`lib ofp oset ofr ${mobile ? 'lib--m ofr--m' : ''}`}>
      <div className="ofr-scroll">
        <div className="ofr-frame">
          <main className="ofr-content">
            {isSearchMode ? (
              <header className="ofr-head ofr-head--search">
                <button type="button" className="ofr-head-btn" aria-label={t('back')} title={t('back')} onClick={exitSearch}>
                  <FeatherIcon name="arrow-left" size={24} />
                </button>
                <form
                  className="ofr-search"
                  role="search"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (searchInput && list.status !== 'loading') {
                      lastTyped.current = searchInput
                      runSearch(searchInput)
                    }
                  }}
                >
                  <input
                    className="ofr-search-input"
                    type="search"
                    autoFocus
                    maxLength={SEARCH_MAX}
                    value={searchInput}
                    placeholder={`${t('searchPlaceholder')}...`}
                    aria-label={t('searchPlaceholder')}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <button type="button" className="ofr-search-clear" aria-label={t('clearSearch')} onClick={() => setSearchInput('')}>
                      <FeatherIcon name="x" size={14} />
                    </button>
                  )}
                  <button type="submit" className="ofr-icon-btn" aria-label={t('search')} disabled={list.status === 'loading' || !searchInput}>
                    <FeatherIcon name="search" size={20} />
                  </button>
                </form>
              </header>
            ) : (
              <>
                <header className="ofr-head">
                  <button type="button" className="ofr-head-btn" aria-label={t('back')} title={t('back')} onClick={() => onOpenPage('more')}>
                    <FeatherIcon name="arrow-left" size={24} />
                  </button>
                  <h1 className="ofr-title">{t('title')}</h1>
                </header>
                {isPerformer && (
                  <div className="ofr-alerts">
                    {me && me.needTagCount > 0 && <div className="ofr-hint">{t('tagsRequired', { count: me.needTagCount })}</div>}
                    <div className="ofr-alert">
                      <span className="ofr-alert-icon">
                        <FeatherIcon name="at-sign" size={18} />
                      </span>
                      <span className="ofr-alert-text">{t('alertTagFriend')}</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {isPerformer && (
              <div className={`ofr-tabs ${isSearchMode ? 'is-search' : ''}`}>
                <Tabs value={isMentions ? 'mentions' : listTab} tabs={tabs} onChange={changeTab} label={t('tabs.label')} />
              </div>
            )}

            {me && firstTime && !isSearchMode && !isMentions && !filtered && (
              <div className="ofr-intro">
                <p className="ofr-intro-main">{t('notice.main')}</p>
                <p className="ofr-intro-sec">
                  {t('notice.sec')}{' '}
                  <a href={`${OF}/terms`} target="_blank" rel="noopener noreferrer">
                    {t('notice.link')}
                  </a>
                </p>
              </div>
            )}

            {me && !wide && isPerformer && (!isSearchMode || firstTime) && <div className="ofr-inline-widgets">{widgets(isMentions ? 'mentions' : listTab, false)}</div>}

            {me && !isSearchMode && formsAvailable && (
              <div className="ofr-filterbar">
                {!list.items.length && !loadingList && listTab === 'users' && !filtered ? (
                  <div className="ofr-fake-search">
                    <input className="ofr-fake-search-input" type="text" readOnly placeholder={`${t('searchUsers')}...`} onFocus={() => goToSearch('users')} onClick={() => goToSearch('users')} />
                    <FeatherIcon name="search" size={20} className="ofr-fake-search-icon" />
                  </div>
                ) : list.items.length || filtered ? (
                  <div className="ofr-filter">
                    <h2 className="ofr-filter-title">{sortTitle}</h2>
                    <button type="button" className="ofr-icon-btn" aria-label={t('search')} title={t('search')} onClick={() => goToSearch(listTab)}>
                      <FeatherIcon name="search" size={20} />
                    </button>
                    <Popover button={<SortIcon />} label={t('sortTooltip')} active={filtered} className="ofr-sort">
                      {(close) => (
                        <>
                          {(['filter', 'sort', 'order'] as const).map((group, gi) => (
                            <div key={group} className={`ofr-pop-group ${gi ? 'has-divider' : ''}`}>
                              {(group === 'filter' ? (['all', 'pending'] as const) : group === 'sort' ? (['date', 'name'] as const) : (['asc', 'desc'] as const)).map((value) => (
                                <label key={value} className="ofr-pop-radio">
                                  <input
                                    type="radio"
                                    name={`rf-${group}-field`}
                                    checked={opts[group] === value}
                                    onChange={() => {
                                      changeOption(group, value)
                                      close()
                                    }}
                                  />
                                  <span className="ofr-radio" aria-hidden="true" />
                                  {t(`${group}.${value}`)}
                                </label>
                              ))}
                            </div>
                          ))}
                        </>
                      )}
                    </Popover>
                  </div>
                ) : null}
              </div>
            )}

            <div className="ofr-body" aria-busy={loadingList || list.more}>
              {body}
            </div>

            {selected.length > 0 && (
              <div className="ofr-selected" role="region" aria-label={t('selected', { count: selected.length, max: SELECT_MAX })}>
                <button type="button" className="ofr-selected-x" aria-label={t('deselect')} title={t('deselect')} onClick={() => setSelected([])}>
                  <FeatherIcon name="x" size={14} />
                </button>
                <span className="ofr-selected-text">{t('selected', { count: selected.length, max: SELECT_MAX })}</span>
                <button type="button" className="ofr-selected-btn" onClick={() => setDialog({ kind: 'addTo' })}>
                  {t('addTo')}
                </button>
              </div>
            )}
          </main>

          {wide && isPerformer && me && (
            <aside className="ofr-sidebar">
              {!isSearchMode && (
                <div className="ofr-side-search">
                  <input
                    className="ofr-side-search-input"
                    type="text"
                    readOnly
                    placeholder={`${t('searchPlaceholder')}...`}
                    onClick={() => goToSearch(isMentions ? 'users' : listTab)}
                    onFocus={() => goToSearch(isMentions ? 'users' : listTab)}
                  />
                  <FeatherIcon name="search" size={20} className="ofr-fake-search-icon" />
                </div>
              )}
              {widgets(tab, true)}
            </aside>
          )}
        </div>
      </div>

      {dialog?.kind === 'guest' && (
        <GuestModal
          of={of}
          canWrite={canWrite}
          say={say}
          item={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={(saved, created) => {
            putOnTop('users', saved)
            if (created) {
              // saveGuest → UPDATE_SELECTION: a new guest is picked on TAG USER.
              setSelected((all) => (all.length >= SELECT_MAX ? all : [...all, { key: selectKey(saved, 'users'), item: saved }]))
              if (tab !== 'users') go({ tab: 'users', searchBy: searchBy ? 'users' : null })
            } else renameSelected(saved)
          }}
          onSearchUsers={() => {
            setDialog(null)
            if (!searchBy) goToSearch('users')
          }}
        />
      )}
      {dialog?.kind === 'link' && (
        <LinkModal
          of={of}
          canWrite={canWrite}
          say={say}
          item={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={(saved, created) => {
            putOnTop('forms', saved)
            if (!created) renameSelected(saved)
          }}
        />
      )}
      {dialog?.kind === 'docs' && (
        <DocsModal
          of={of}
          canWrite={canWrite}
          item={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            putOnTop('forms', saved)
            renameSelected(saved)
          }}
        />
      )}
      {dialog?.kind === 'chooser' && <ChooserModal canWrite={canWrite} onClose={() => setDialog(null)} onLink={() => setDialog({ kind: 'link', item: null })} />}
      {dialog?.kind === 'addTo' && <AddToModal onClose={() => setDialog(null)} />}
      {toast}
      {mobile && mobileNav}
    </div>
  )
}

// ── Pieces ──────────────────────────────────────────────────────────────────

function MentionsLabel({ text, badge }: { text: string; badge: string }) {
  return (
    <span className="ofr-tab-mentions">
      {text}
      <span className="ofr-tab-badge">{badge}</span>
    </span>
  )
}

function ErrorBox({ text, onRetry }: { text: string; onRetry: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  return (
    <div className="ofr-error" role="alert">
      <span>{text}</span>
      <button type="button" className="ofr-text-link" onClick={onRetry}>
        {t('retry')}
      </button>
    </div>
  )
}

function EmptyState({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="ofr-empty">
      <FeatherIcon name={icon} size={72} className="ofr-empty-icon" />
      {text && <p>{text}</p>}
    </div>
  )
}

/** GroupWidgets: "Invite new user" and "New release form". */
function GroupWidgets({
  showUsers,
  showForms,
  busy,
  canWrite,
  uploads,
  onInvite,
  onNewForm,
}: {
  showUsers: boolean
  showForms: boolean
  busy: boolean
  canWrite: boolean
  uploads: boolean
  onInvite: () => void
  onNewForm: () => void
}) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  if (!showUsers && !showForms) return null
  return (
    <div className="ofr-widgets">
      {showUsers && (
        <section className="ofr-widget">
          <h3 className="ofr-widget-title">{t('widgets.inviteTitle')}</h3>
          <p className="ofr-widget-hint">{t('widgets.inviteHint')}</p>
          <button type="button" className="ofr-widget-btn" disabled={busy || !canWrite} onClick={onInvite}>
            {t('widgets.inviteButton')}
          </button>
        </section>
      )}
      {showForms && (
        <section className="ofr-widget">
          <h3 className="ofr-widget-title">{t('widgets.newFormTitle')}</h3>
          <p className="ofr-widget-hint">
            {t('widgets.newFormHint')}
            {uploads && <span> {t('widgets.newFormUploadHint')}</span>}
          </p>
          <button type="button" className="ofr-widget-btn" disabled={busy || !canWrite} onClick={onNewForm}>
            {t('widgets.newFormButton')}
          </button>
        </section>
      )}
    </div>
  )
}

interface RowProps {
  item: RfItem
  checked: boolean
  disabled: boolean
  canWrite: boolean
  onToggle: () => void
  onHide: () => void
}

/** The round check, the row text and the ⋮ menu of a b-rows-lists__item. */
function RowFrame({ checked, disabled, onToggle, icon, title, sub, menu }: Pick<RowProps, 'checked' | 'disabled' | 'onToggle'> & { icon: ReactNode; title: string; sub: ReactNode; menu: ReactNode }) {
  return (
    <div className={`ofr-row ${disabled ? 'is-disabled' : ''} ${checked ? 'is-checked' : ''}`} role="listitem">
      <label className="ofr-row-label">
        <input type="checkbox" className="ofr-row-check" checked={checked} disabled={disabled} onChange={onToggle} aria-label={title} />
        <span className="ofr-check" aria-hidden="true">
          <FeatherIcon name="check" size={12} />
        </span>
        <span className="ofr-row-icon">{icon}</span>
        <span className="ofr-row-text">
          <span className="ofr-row-name">{title}</span>
          <span className="ofr-row-sub">{sub}</span>
        </span>
      </label>
      {menu}
    </div>
  )
}

function Dots({ items }: { items: (close: () => void) => ReactNode }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  return (
    <Popover button={<FeatherIcon name="more-vertical" size={20} />} label={t('menu.more')} className="ofr-dots">
      {items}
    </Popover>
  )
}

function MenuItem({ children, onClick, close, disabled, danger }: { children: ReactNode; onClick: () => void; close: () => void; disabled?: boolean; danger?: boolean }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`ofr-pop-item ${danger ? 'is-danger' : ''}`}
      disabled={disabled}
      onClick={() => {
        close()
        onClick()
      }}
    >
      {children}
    </button>
  )
}

const Sub = ({ parts }: { parts: ReactNode[] }) => (
  <>
    {parts.map((p, i) => (
      <span key={i} className="ofr-dot-item">
        {p}
      </span>
    ))}
  </>
)

/** TAG USER: a tagged creator or a guest invitation. */
function UserRow({ item, onCopy, onEdit, canWrite, onHide, ...rest }: RowProps & { onCopy: (kind: 'guest' | 'profile' | 'username') => void; onEdit: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  const guest = isGuest(item)
  const username = item.user?.username
  const sub = guest ? [t('guest')] : [isIdentityVerified(item) ? t('verified') : t('notVerified'), ...(username ? [`@${username}`] : [])]
  const hidden = isHidden(item)
  return (
    <RowFrame
      {...rest}
      title={item.name}
      icon={
        guest ? (
          <span className="ofr-guest-avatar">
            <FeatherIcon name="user" size={20} />
          </span>
        ) : (
          <Avatar user={{ name: item.user?.name ?? item.name, avatar: item.user?.avatar ?? '' }} size={36} />
        )
      }
      sub={<Sub parts={sub} />}
      menu={
        <Dots
          items={(close) => (
            <>
              {guest && !rest.disabled ? (
                <>
                  <MenuItem close={close} onClick={() => onCopy('guest')}>
                    {t('menu.copyInvitation')}
                  </MenuItem>
                  <MenuItem close={close} onClick={onEdit} disabled={!canWrite}>
                    {t('menu.editTag')}
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItem close={close} onClick={() => onCopy('profile')}>
                    {t('menu.copyProfile')}
                  </MenuItem>
                  <MenuItem close={close} onClick={() => onCopy('username')}>
                    {t('menu.copyUsername')}
                  </MenuItem>
                </>
              )}
              <hr className="ofr-pop-divider" />
              <MenuItem close={close} onClick={onHide} disabled={!canWrite} danger>
                {hidden ? t('menu.unhide') : t('menu.hide')}
              </MenuItem>
            </>
          )}
        />
      }
    />
  )
}

/** RELEASE FORM: a signing link or an uploaded release form. */
function FormRow({ item, onCopy, onEdit, canWrite, onHide, lang, ...rest }: RowProps & { onCopy: () => void; onEdit: () => void; lang: string }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  const link = isLinkForm(item.type)
  const ago = fromNow(item.date)
  const agoText = ago ? t(`fromNow.${ago.key}`, 'count' in ago ? { count: ago.count } : {}) : ''
  let sub: ReactNode[]
  if (link) {
    const state = isViewed(item) ? t('status.viewed') : isSigned(item) ? t('status.signed') : t('status.notSigned')
    const when =
      isLinkExpired(item) && !isSigned(item) ? (
        t('status.pastDue')
      ) : showExpirationWarning(item) ? (
        <>
          {agoText} <FeatherIcon name="alert-circle" size={14} className="ofr-warn" />
        </>
      ) : (
        agoText
      )
    sub = [state, ...(item.userName ? [`@${item.userName}`] : []), when]
  } else {
    const state =
      item.status === 'approved' && item.approveDate ? (
        <>
          {item.approveDate} <FeatherIcon name="check" size={14} className="ofr-ok" />
        </>
      ) : item.status === 'failed' ? (
        `${t('status.failed')}: ${t('status.idDocument')}`
      ) : (
        t('status.notVerified')
      )
    sub = [state, `${t('status.created')}: ${formatDay(item.date, lang)}`]
  }
  const showCopy = showCopyLinkBtn(item)
  const showEdit = showEditBtn(item)
  const hidden = isHidden(item)
  return (
    <RowFrame
      {...rest}
      title={item.name}
      icon={
        <span className="ofr-form-icon">
          <FeatherIcon name={link ? 'link' : 'file-text'} size={22} />
        </span>
      }
      sub={<Sub parts={sub} />}
      menu={
        <Dots
          items={(close) => (
            <>
              {(!rest.disabled || showCopy || showEdit) && (
                <>
                  {showCopy && (
                    <MenuItem close={close} onClick={onCopy}>
                      {t('menu.copyLink')}
                    </MenuItem>
                  )}
                  {showEdit && (
                    <MenuItem close={close} onClick={onEdit} disabled={!canWrite}>
                      {link ? t('menu.editLink') : t('menu.editForm')}
                    </MenuItem>
                  )}
                  <hr className="ofr-pop-divider" />
                </>
              )}
              <MenuItem close={close} onClick={onHide} disabled={!canWrite} danger>
                {hidden ? t('menu.unhide') : t('menu.hide')}
              </MenuItem>
            </>
          )}
        />
      }
    />
  )
}

/** MENTIONS: release-forms/mentions as OnlyFans' notification rows. */
function MentionsList({ state, users, onRetry, onMore, lang }: { state: MentionsState; users: UserCache; onRetry: () => void; onMore: () => void; lang: string }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  if (state.status === 'error') return <ErrorBox text={t('loadFailed', { error: state.error })} onRetry={onRetry} />
  if (state.status !== 'ready') return <LoadingBlock label={t('loading')} />
  if (!state.rows.length) return <EmptyState icon="at-sign" text={t('empty.mentions')} />
  return (
    <div className="ofr-mentions" role="list">
      {state.rows.map((n, i) => {
        const v = fanView(n, users)
        const action = actionOf(n)
        const time = timeLabel(createdAt(n), lang)
        const timeText = !time ? '' : 'date' in time ? time.date : time.key === 'justNow' ? t('time.justNow') : t(`time.${time.key}`, { count: time.count })
        return (
          <div key={n.id == null ? `i${i}` : String(n.id)} className="ofr-mention" role="listitem">
            <Avatar user={{ name: v.name || t('user'), avatar: v.avatar }} size={48} />
            <div className="ofr-mention-body">
              <div className="ofr-mention-head">
                <span className="ofr-mention-name">{v.name || t('user')}</span>
                {v.verified && <FeatherIcon name="check-circle" size={14} className="ofr-mention-verified" />}
                {v.handle && <span className="ofr-mention-handle">{v.handle}</span>}
              </div>
              {(action.text || action.key) && <div className="ofr-mention-text">{action.text ?? action.key}</div>}
              {timeText && <div className="ofr-mention-time">{timeText}</div>}
            </div>
          </div>
        )
      })}
      {state.hasMore && (
        <div className="ofr-more">
          {state.more ? (
            <Spinner size={22} label={t('loading')} />
          ) : state.error ? (
            <button type="button" className="ofr-text-link" onClick={onMore}>
              {t('retry')}
            </button>
          ) : (
            <Sentinel onVisible={onMore} token={state.rows.length} />
          )}
        </div>
      )}
    </div>
  )
}
